import { FC, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { QRCode } from 'react-qrcode-logo';
import AppIconBase from 'src/components/AppIcon';
import Button from 'src/components/Button';
import ExternalLink from 'src/components/ExternalLink';
import ModalLayout, { MobileModalType, ModalContent, ModalHeader, ModalHeaderDescriptionRow, ModalHeaderTitle } from 'src/components/ModalLayout';
import { webToAppLastModalShownAtom } from 'src/stores/webToApp/atom';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { WebToAppModalProps, WebToAppModalType } from 'src/types/WebToApp';
import getDeviceInfo from 'src/utils/device/info';
import { pushEvent } from 'src/utils/event-gateway';
import { CampaignId, getWebToAppUrl } from 'src/utils/webToApp';
const RootStyle = styled.div`
  color: white;
  width: 400px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    width: 100%;
  }
`;
const LogoWrap = styled.div`
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const AppIcon = styled(AppIconBase)`
  width: 54px;
  height: 54px;
`;
const Title = styled.p`
  color: ${({
  theme
}) => theme.color.contentDeep};
  text-align: center;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 26px; /* 130% */
  margin-bottom: 8px;
`;
const Description = styled.p`
  color: ${({
  theme
}) => theme.color.content9};
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`;
const ButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`;
const QRCodeWrap = styled.div`
  display: flex;
  padding: 24px;
  margin: 24px 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  align-self: stretch;

  border-radius: 20px;
  background: #fff;
`;
const QRCodeTitle = styled.p`
  color: ${({
  theme
}) => theme.color.content7};
  text-align: center;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
`;
const BrandWrap = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`;
const BrandLink = styled(ExternalLink)`
  text-decoration: none;
`;
const IcApple = styled(dynamic(() => import('src/assets/images/imgBrandApple.svg')))`
  width: 24px;
  height: 24px;
  margin: 0 12px;
  fill: ${({
  theme
}) => theme.color.contentBright};
`;
const IcGooglePlay = styled(dynamic(() => import('src/assets/images/imgBrandGooglePlay.svg')))`
  width: 24px;
  height: 24px;
  fill: ${({
  theme
}) => theme.color.contentBright};
`;
const webToAppTypeConfigs: { [key in WebToAppModalType]: {
  title: string;
  description: string;
  campaignId: CampaignId;
  tab: string;
  page: string;
} } = {
  [WebToAppModalType.BASIC]: {
    title: 'web_to_app_tilte',
    description: 'web_to_app_desc',
    campaignId: 'web_to_app_home',
    tab: 'mirror',
    page: 'main'
  },
  [WebToAppModalType.EFFECT]: {
    title: 'web_to_app_effect_title',
    description: 'web_to_app_effect_desc',
    campaignId: 'web_to_app_effect',
    tab: 'mirror',
    page: 'main'
  },
  [WebToAppModalType.MESSAGE]: {
    title: 'web_to_app_message_title',
    description: 'web_to_app_message_desc',
    campaignId: 'web_to_app_message',
    tab: 'history',
    page: 'main'
  },
  [WebToAppModalType.LIVENESS_FAILED]: {
    title: 'web_to_app_FaceTec_failure_title',
    description: 'web_to_app_FaceTec_failure_desc',
    campaignId: 'web_to_app_liveness_failed',
    tab: 'onboarding',
    page: 'liveness_verifying'
  },
  [WebToAppModalType.LIVENESS_PENDING]: {
    title: 'web_to_app_FaceTec_delay_title',
    description: 'web_to_app_FaceTec_delay_desc',
    campaignId: 'web_to_app_liveness_pending',
    tab: 'onboarding',
    page: 'liveness_verifying'
  },
  [WebToAppModalType.LIVENESS_YOTI_OVERCONSTRAINED]: {
    title: 'web_to_app_FaceTec_failure_title',
    description: 'web_to_app_FaceTec_failure_desc',
    campaignId: 'web_to_app_overconstrained',
    tab: 'onboarding',
    page: 'liveness_check'
  },
  [WebToAppModalType.LIVENESS_YOTI_UNSUPPORTED_BROWSER]: {
    title: 'web_to_app_FaceTec_failure_title',
    description: 'web_to_app_FaceTec_failure_desc',
    campaignId: 'web_to_app_unsupported_browser',
    tab: 'onboarding',
    page: 'liveness_check'
  }
} as const;
const WebToAppModal: FC<WebToAppModalProps> = ({
  type,
  onClose,
  trigger
}) => {
  const {
    t
  } = useTranslation();
  const keys = webToAppTypeConfigs[type];
  const setLastModalShown = useSetAtom(webToAppLastModalShownAtom);
  const {
    isMobile
  } = getDeviceInfo();
  useEffect(() => {
    if (type === WebToAppModalType.BASIC) {
      const now = new Date();
      setLastModalShown(now);
    }
  }, [type, setLastModalShown]);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.WEB2APP,
      eventName: EVENT_NAME.ANYWHERE__IMPRESSION_TO_APP_MODAL,
      eventParams: {
        action_category: 'impression',
        tab: keys.tab,
        page: keys.page,
        target: 'to_app_modal',
        referrer: 'web_to_app',
        type: isMobile ? 'button' : 'qr_code',
        content_id: type,
        trigger
      }
    });
  }, [type, isMobile, keys.tab, trigger, keys.page]);
  const handleClick = useCallback(() => {
    const url = getWebToAppUrl(keys.campaignId);
    pushEvent({
      eventType: EVENT_TYPE.WEB2APP,
      eventName: EVENT_NAME.ANYWHERE__CLICK_TO_APP_BTN,
      eventParams: {
        action_category: 'click',
        tab: type === WebToAppModalType.MESSAGE ? 'history' : 'mirror',
        page: keys.page,
        target: 'to_app_btn',
        content_id: type,
        button_type: 'universal',
        referrer: 'web_to_app',
        trigger
      }
    });
    window.open(url.toString(), '_blank');
  }, [keys.campaignId, type, trigger, keys.page]);
  return <ModalLayout mobileType={MobileModalType.BOTTOM_SHEET} isDim onClose={onClose} data-testid='Web-To-App-Modal' data-sentry-element="ModalLayout" data-sentry-component="WebToAppModal" data-sentry-source-file="index.tsx">
      <RootStyle data-sentry-element="RootStyle" data-sentry-source-file="index.tsx">
        {isMobile && <>
            <LogoWrap>
              <AppIcon />
            </LogoWrap>
            <ModalContent>
              <Title>{t(keys.title)}</Title>
              <Description>{t(keys.description)}</Description>
            </ModalContent>
            <ButtonWrap>
              <Button id='anywhere__click_to_app_modal_btn' color='primary' onClick={handleClick}>
                {t('web_to_app_button')}
              </Button>
            </ButtonWrap>
          </>}
        {!isMobile && <>
            <ModalHeader>
              <ModalHeaderTitle mobileType={MobileModalType.BOTTOM_SHEET}>
                {t(keys.title)}
              </ModalHeaderTitle>
              <ModalHeaderDescriptionRow>{t(keys.description)}</ModalHeaderDescriptionRow>
            </ModalHeader>
            <ModalContent>
              <QRCodeWrap>
                <QRCodeTitle>{t('SETTINGS_DOWNLOAD_APP')}</QRCodeTitle>
                <QRCode value={getWebToAppUrl(keys.campaignId).toString()} logoWidth={46} logoHeight={46} qrStyle='squares' logoImage='/images/modal/cheeroRedirect/imgAzarSymbol.svg' />

                <BrandWrap>
                  <BrandLink href={getWebToAppUrl(keys.campaignId).toString()}>
                    <IcApple onClick={() => pushEvent({
                  eventType: EVENT_TYPE.WEB2APP,
                  eventName: EVENT_NAME.ANYWHERE__CLICK_TO_APP_BTN,
                  eventParams: {
                    action_category: 'click',
                    tab: keys.tab,
                    page: keys.page,
                    target: 'to_app_btn',
                    button_type: 'appstore',
                    referrer: 'in_qr_code',
                    trigger
                  }
                })} />
                  </BrandLink>
                  <BrandLink href={getWebToAppUrl(keys.campaignId).toString()}>
                    <IcGooglePlay onClick={() => pushEvent({
                  eventType: EVENT_TYPE.WEB2APP,
                  eventName: EVENT_NAME.ANYWHERE__CLICK_TO_APP_BTN,
                  eventParams: {
                    action_category: 'click',
                    tab: keys.tab,
                    page: keys.page,
                    target: 'to_app_btn',
                    button_type: 'playstore',
                    referrer: 'in_qr_code',
                    trigger
                  }
                })} />
                  </BrandLink>
                </BrandWrap>
              </QRCodeWrap>
            </ModalContent>
          </>}
      </RootStyle>
    </ModalLayout>;
};
export default WebToAppModal;